import { ChangeEvent, useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Footer from "../../components/ui/Footer";
import Heading from "../../components/ui/Heading";
import Preview__img from "../../assets/images/upload_preview.png";
import GuidelinePicture from "../../assets/images/Guideline-picture.png";
import "./UserProfile.css";
import resources from "../../resources.json";
import Image from "../../components/ui/Image";
import { useUserProfile } from "./useUserProfile";
import { useCountryCodes } from "./useCountryCodes";
import { useUpdateUserProfile } from "./useUpdateUserProfile";
import SpinnerMini from "../../components/ui/SpinnerMini";
import { formatPhoneNumber, getImageSize } from "../../utils";
import { useProfilePicture } from "./useProfilePicture";
import {
  emailRegex,
  IMAGE_HEIGHT,
  IMAGE_SIZE,
  IMAGE_TYPES,
  IMAGE_WIDTH,
  passwordRegex,
  phoneNumberRegex,
} from "../../utils/constants";
import toast from "react-hot-toast";
import Container from "../../components/ui/Container";
import ButtonsContainer from "../../components/ui/ButtonsContainer";
import ImageInput from "../../components/ui/ImageInput";
import AppLoader from "../../components/ui/AppLoader";
import TextInput from "../../components/Inputs/TextInput";
import PasswordInput from "../../components/Inputs/PasswordInput";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Spinner from "../../components/ui/Spinner";
import FormContainer from "components/ui/FormContainer";

const schema = yup.object().shape({
  userName: yup
    .string()
    .required(resources.errorMessage.USER_NAME_REQUIRED_ERROR),
  firstName: yup
    .string()
    .required(resources.errorMessage.FIRST_NAME_REQUIRED_ERROR)
    .max(100, resources.errorMessage.FIRST_NAME_LENGTH_ERROR),
  lastName: yup
    .string()
    .required(resources.errorMessage.LAST_NAME_REQUIRED_ERROR)
    .max(100, resources.errorMessage.LAST_NAME_LENGTH_ERROR),
  userEmail: yup
    .string()
    .required(resources.errorMessage.EMAIL_REQUIRED_ERROR)
    .matches(emailRegex, resources.errorMessage.EMAIL_INVALID_ERROR)
    .max(100, resources.errorMessage.EMAIL_LENGTH_ERROR),
  phoneNumber: yup
    .string()
    .notRequired()
    .test(
      "phoneNumber",
      resources.errorMessage.PHONE_NUMBER_INVALID_ERROR,
      function (value) {
        if (value && !phoneNumberRegex.test(value)) {
          return false;
        }
        return true;
      },
    ),
  countryCode: yup
    .string()
    .required(resources.errorMessage.USER_NAME_REQUIRED_ERROR),
  currentPassword: yup
    .string()
    .test(
      "currentPassword-test",
      resources.errorMessage.CURRENT_PASSWORD_REQUIRED_ERROR,
      function (value, { parent }) {
        if (!value && parent.newPassword) {
          return false;
        }
        return true;
      },
    ),

  newPassword: yup
    .string()
    .test("newPassword", "", function (value, { parent }) {
      if (!value && parent.currentPassword) {
        return this.createError({
          message: resources.errorMessage.NEW_PASSWORD_REQUIRED_ERROR,
        });
      }

      if (value && !value.match(passwordRegex)) {
        return this.createError({
          message: resources.errorMessage.PASSWORD_VALIDATION_ERROR,
        });
      }

      if (value && value === parent.currentPassword) {
        return this.createError({
          message: resources.errorMessage.PASSWORDS_MATCH_ERROR,
        });
      }

      return true;
    }),
  confirmPassword: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "confirmPassword",
      resources.errorMessage.PASSWORDS_NOT_MATCH_ERROR,
      function (value, { parent }) {
        if ((value || parent.newPassword) && value !== parent.newPassword) {
          return false;
        }
        return true;
      },
    ),
});

type UserProfileFormData = yup.InferType<typeof schema>;

export default function UserProfile() {
  const { profilePicture } = useProfilePicture();
  const { data: userProfileData, isLoading: isUserProfileLoading } =
    useUserProfile();
  const { countryCodes } = useCountryCodes();
  const {
    updateUserProfile,
    isLoading: updateUserLoading,
    isSuccess: isUpdateSuccess,
  } = useUpdateUserProfile();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    setValue,
    resetField,
  } = useForm<UserProfileFormData>({
    defaultValues: {
      countryCode: "+1",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      userEmail: "",
      userName: "",
    },
    resolver: yupResolver(schema),
  });

  const [imgError, setImgError] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [isProfileImageDeleted, setIsProfileImageDeleted] =
    useState<boolean>(false);

  const isProfileImageAvailable =
    userProfileData?.isProfileImageAvailable ?? false;

  const onSubmit: SubmitHandler<UserProfileFormData> = (data) => {
    if (!!imgError) return toast.error(imgError);

    updateUserProfile({
      userDetailsInfo: JSON.stringify({
        ...data,
        isProfileImageDeleted: isProfileImageDeleted,
      }),
      profileImage,
    });
  };

  useEffect(() => {
    if (userProfileData) {
      reset({
        ...userProfileData,
        countryCode:
          userProfileData?.countryCode === null
            ? "+1"
            : userProfileData?.countryCode,
      });
    }
  }, [reset, userProfileData]);

  useEffect(() => {
    if (isUpdateSuccess) {
      // reset({ confirmPassword: '', currentPassword: '', newPassword: '' })
      resetField("confirmPassword");
      resetField("currentPassword");
      resetField("newPassword");
    }
  }, [isUpdateSuccess, resetField]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setImgError("");
    if (!!e.target.files) {
      const size = e.target.files[0].size / (1024 * 1024);
      const res = await getImageSize(e.target?.files[0]);
      const { height, width } = res;
      if (height > IMAGE_HEIGHT || width > IMAGE_WIDTH || size > IMAGE_SIZE) {
        setImgError(
          `Image dimensions should be ${IMAGE_HEIGHT}x${IMAGE_WIDTH} and size less than ${IMAGE_SIZE} mb`,
        );
      }
      setProfileImage(e.target.files[0]);
      setIsProfileImageDeleted(false);
    }
    e.target.value = "";
  };

  const deleteImage = () => {
    setImgError("");
    setIsProfileImageDeleted(true);
    setProfileImage(null);
    // imgRef.current.value = null;
  };

  return (
    <>
      {updateUserLoading && <AppLoader />}
      <Heading>
        <Heading.Title>{resources.title.MY_PROFILE_TITLE}</Heading.Title>
      </Heading>
      {isUserProfileLoading ? (
        <Spinner />
      ) : (
        <FormContainer hasFooter>
          <form onSubmit={handleSubmit(onSubmit)} className="profile__wrapper">
            <Card>
              <div className="user__profile__container">
                <Container className="col col-3">
                  <TextInput
                    placeholder={resources.label.USER_NAME_LABEL}
                    id="username"
                    register={register("userName")}
                    error={errors.userName?.message}
                    label={resources.label.USER_NAME_LABEL}
                    disabled
                  />

                  <TextInput
                    placeholder={resources.label.FIRST_NAME_LABEL}
                    id="firstName"
                    register={register("firstName", {
                      required:
                        resources.errorMessage.FIRST_NAME_REQUIRED_ERROR,
                      maxLength: {
                        value: 100,
                        message: resources.errorMessage.FIRST_NAME_LENGTH_ERROR,
                      },
                    })}
                    error={errors.firstName?.message}
                    label={resources.label.FIRST_NAME_LABEL}
                    required
                  />

                  <TextInput
                    placeholder={resources.label.LAST_NAME_LABEL}
                    id="lastName"
                    register={register("lastName")}
                    error={errors.lastName?.message}
                    label={resources.label.LAST_NAME_LABEL}
                    required
                  />

                  <TextInput
                    placeholder={resources.label.EMAIL_LABEL}
                    id="email"
                    register={register("userEmail")}
                    error={errors.userEmail?.message}
                    label={resources.label.EMAIL_LABEL}
                    required
                  />

                  <PhoneNumberInput
                    label={resources.label.PHONE_LABEL}
                    countryCodes={countryCodes ?? []}
                    register={register("phoneNumber", {
                      onChange: (e) =>
                        setValue(
                          "phoneNumber",
                          formatPhoneNumber(e.target.value),
                        ),
                    })}
                    countryCodeRegister={register("countryCode")}
                    error={errors.phoneNumber?.message}
                  />
                </Container>

                <Container className="col col-3">
                  <PasswordInput
                    error={errors.currentPassword?.message}
                    placeholder={resources.label.CURRENT_PASSWORD}
                    id="currentPassword"
                    register={register("currentPassword")}
                    label={resources.label.CURRENT_PASSWORD}
                  />

                  <PasswordInput
                    error={errors.newPassword?.message}
                    placeholder={resources.label.NEW_PASSWORD}
                    id="newPassword"
                    register={register("newPassword")}
                    label={resources.label.NEW_PASSWORD}
                    hasTooltip
                    content={resources.tooltipMessage.PASSWORD_VALIDATION}
                  />

                  <PasswordInput
                    error={errors.confirmPassword?.message}
                    placeholder={resources.label.CONFIRM_NEW_PASSWORD}
                    id="confirmPassword"
                    register={register("confirmPassword", {
                      validate: (value) =>
                        value === getValues().newPassword ||
                        resources.errorMessage.PASSWORDS_NOT_MATCH_ERROR,
                    })}
                    label={resources.label.CONFIRM_NEW_PASSWORD}
                    hasTooltip
                    content={resources.tooltipMessage.PASSWORD_VALIDATION}
                  />
                </Container>
              </div>
            </Card>
            <Card>
              <div className="upload__profile__wrapper">
                <div className="upload__image__box__wrapper">
                  <ImageInput
                    label={resources.label.PROFILE__PICTURE__LABEL}
                    handleFileChange={handleFileChange}
                    imageHeight={IMAGE_HEIGHT}
                    imageWidth={IMAGE_WIDTH}
                    imageSize={IMAGE_SIZE}
                    imageTypes={IMAGE_TYPES}
                    direction="vertical"
                    id="file-input-field"
                  />
                  <div className="upload__img__wrapper">
                    {!isProfileImageDeleted ? (
                      <Image
                        src={
                          !!profileImage
                            ? URL.createObjectURL(profileImage)
                            : isProfileImageAvailable
                              ? profilePicture
                              : Preview__img
                        }
                        alt="upload img"
                        className="upload__img"
                      />
                    ) : (
                      <Image
                        src={Preview__img}
                        alt="upload img"
                        className="upload__img"
                      />
                    )}

                    {(!!profileImage || !!isProfileImageAvailable) &&
                      !isProfileImageDeleted && (
                        <div className="img__overlay">
                          <Button
                            className="trash__icon__container"
                            type="button"
                            onClick={deleteImage}
                          >
                            <i className="icon-trash"></i>
                          </Button>
                        </div>
                      )}
                  </div>
                </div>
                <div className="upload__guidelines__container">
                  <p className="upload__profile__label">
                    {resources.label.PROFILE__PICTURE__GUIDELINES__LABEL}
                  </p>
                  <div>
                    <Image
                      src={GuidelinePicture}
                      alt="guideline-pics"
                      className="upload__guidelines__img"
                    />
                  </div>
                </div>
              </div>
            </Card>
            <Footer>
              <ButtonsContainer>
                <Button buttonType="primary" disabled={updateUserLoading}>
                  {updateUserLoading ? <SpinnerMini /> : "Save Changes"}
                </Button>
              </ButtonsContainer>
            </Footer>
          </form>
        </FormContainer>
      )}
    </>
  );
}
